import type { PanelPosition } from "./types";
import { PART_TYPE_IDS, BURNER_POSITIONS } from "../../models";
import {
  PANEL_COLORS,
  COMPONENT_COLORS,
  SidewallSvgColors,
} from "../../theme/colors";
import { Viewer3DSettings } from "../../../../shared/theme";

// Helper to convert degrees to radians
const deg2rad = (deg: number) => (deg * Math.PI) / 180;

// Empty position color (gray)
export const EMPTY_POSITION_COLOR = COMPONENT_COLORS.EMPTY;

// Hover/Selection highlight intensity (from shared 3D viewer settings)
export const HOVER_EMISSIVE_INTENSITY =
  Viewer3DSettings.HOVER_EMISSIVE_INTENSITY;
export const SELECTION_EMISSIVE_INTENSITY =
  Viewer3DSettings.SELECTION_EMISSIVE_INTENSITY;

// Geometry dimensions for the sidewall
export const SIDEWALL_DIMENSIONS = {
  // Panel ring dimensions
  panelInnerRadius: 0.6, // Inner edge of panels
  panelOuterRadius: 0.95, // Outer edge of panels
  panelHeight: 0.08, // Extrusion height

  // Sump dimensions (below panels)
  sumpInnerRadius: 0.2, // Sump area inner
  sumpOuterRadius: 0.55, // Sump area outer (before panels)
  sumpHeight: 0.06,

  // Special components (archway, slag door) at outer edge
  specialRadius: 1.0,

  // Shell dimensions (concentric rings)
  lowerShellRadius: 1.05,
  upperShellRadius: 1.15,
};

// 12 panels distributed evenly (30 degrees each)
// Panel 1 centered at 90° (aligned with Archway), going clockwise
const PANEL_ANGLE_SPAN = 30; // 360 / 12 = 30 degrees each
const PANEL_1_START_ANGLE = 75; // Panel 1 starts at 75°, ends at 105° (centered at 90°)

// Generate panel positions (1-12)
const generatePanelPositions = (): PanelPosition[] => {
  const positions: PanelPosition[] = [];

  for (let i = 1; i <= 12; i++) {
    // Calculate start and end angles for this panel
    // Panel 1 centered at 90° (75° to 105°), going clockwise (increasing angles)
    const startAngle = deg2rad(
      PANEL_1_START_ANGLE + (i - 1) * PANEL_ANGLE_SPAN,
    );
    const endAngle = deg2rad(PANEL_1_START_ANGLE + i * PANEL_ANGLE_SPAN);

    const hasBurner = (BURNER_POSITIONS as readonly number[]).includes(i);

    positions.push({
      id: hasBurner
        ? PART_TYPE_IDS.SIDEWALL_BURNER_PANEL
        : PART_TYPE_IDS.SIDEWALL_PANEL,
      name: `Sidewall ${i}${hasBurner ? ` (#${BURNER_POSITIONS.indexOf(i as 3 | 5 | 9) + 1} Burner)` : ""}`,
      positionId: i,
      startAngle,
      endAngle,
      innerRadius: SIDEWALL_DIMENSIONS.panelInnerRadius,
      outerRadius: SIDEWALL_DIMENSIONS.panelOuterRadius,
      color: PANEL_COLORS[i] ?? EMPTY_POSITION_COLOR,
      height: SIDEWALL_DIMENSIONS.panelHeight,
      ring: "panel",
      hasBurner,
    });
  }

  return positions;
};

export const PANEL_POSITIONS: PanelPosition[] = generatePanelPositions();

// Camera settings for 3D view
export const CAMERA_SETTINGS = {
  position: [0, 1.8, 1.8] as [number, number, number],
  fov: 50,
  near: 0.1,
  far: 100,
};

// OrbitControls settings
export const ORBIT_CONTROLS_SETTINGS = {
  enablePan: true,
  enableZoom: true,
  enableRotate: true,
  minDistance: 1,
  maxDistance: 10,
  minPolarAngle: 0,
  maxPolarAngle: Math.PI / 2.2, // ~81 degrees - prevent going under
  enableDamping: true,
  dampingFactor: 0.05,
  target: [-0.15, 0.4, 0] as [number, number, number], // Offset to center on model geometry
};

// Model position offset (to center the GLB model)
export const MODEL_POSITION = [0, 0, 0] as [number, number, number];

// Lighting settings
export const LIGHTING_SETTINGS = {
  ambientIntensity: 0.6,
  directionalIntensity: 0.8,
  directionalPosition: [5, 10, 5] as [number, number, number],
};

// UI Colors for SVG thumbnail (from theme colors)
export const SVG_UI_COLORS = {
  BACKGROUND: SidewallSvgColors.BACKGROUND, // Dark gray background
  STROKE: SidewallSvgColors.STROKE, // Dark stroke for borders
  CENTER_AREA: SidewallSvgColors.CENTER_AREA, // Medium gray for center
};

// =============================================================================
// SVG THUMBNAIL CONSTANTS
// Geometry and layout constants for the miniature SVG representation
// =============================================================================

/**
 * SVG-specific radii (different from 3D model - simplified representation)
 * These define the concentric rings in the 2D thumbnail view
 */
export const SVG_THUMBNAIL_RADII = {
  RING_OUTER: 1.15, // Shell around panels
  SUMP_OUTER: 1.35, // Sumps/Archway protrude beyond the ring
  SLAG_DOOR_OUTER: 1.55, // Slag door outer boundary (surrounds archway)
} as const;

/**
 * SVG thumbnail angles (in degrees) for component positioning
 * Note: Archway is at top (90°), Sumps are at bottom (270°)
 */
export const SVG_THUMBNAIL_ANGLES = {
  // Archway position (opposite to sumps, at top) - Position 16
  ARCHWAY_START: 75, // degrees
  ARCHWAY_END: 105, // degrees
  // Sump positions (narrower in SVG - 30° each, centered at 270°)
  SUMP_LEFT_START: 225, // degrees
  SUMP_LEFT_END: 255, // degrees
  SUMP_RIGHT_START: 255, // degrees
  SUMP_RIGHT_END: 285, // degrees
  SUMP_CENTER_START: 285, // degrees
  SUMP_CENTER_END: 315, // degrees
} as const;

/**
 * SVG thumbnail layout settings
 * Controls scale, stroke, and element positioning within the thumbnail
 */
export const SVG_THUMBNAIL_LAYOUT = {
  // Scale factor to fit model within thumbnail (with padding)
  SCALE_FACTOR: 0.62,
  // Stroke width for all SVG elements
  STROKE_WIDTH: 0.5,
  // Burner indicator position (ratio from inner to outer edge of panel)
  BURNER_POSITION_RATIO: 0.3, // 30% from inner edge
  // Panel label position (ratio from inner to outer edge of panel)
  LABEL_POSITION_RATIO: 0.75, // 75% from inner edge
  // Burner indicator circle radius (in SVG units)
  BURNER_INDICATOR_RADIUS: 3,
} as const;

/**
 * SVG thumbnail font settings
 * Responsive font sizes based on thumbnail size
 */
export const SVG_THUMBNAIL_FONTS = {
  // Font size for large thumbnails (size > threshold)
  LARGE_SIZE: 10,
  // Font size for small thumbnails (size <= threshold)
  SMALL_SIZE: 6,
  // Threshold (in pixels) to switch between font sizes
  SIZE_THRESHOLD: 60,
} as const;
